/* eslint-disable react/display-name */
import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import CategoryComponent from '../components/Category/Category'
import CustomLoader from '../components/Loader/CustomLoader'
// core components
import Header from '../components/Headers/Header'
import { getRestaurantDetail, deleteCategory } from '../apollo'
import DataTable from '../components/DataTable'
import orderBy from 'lodash/orderBy'
import SearchBar from '../components/TableHeader/SearchBar'
import useGlobalStyles from '../utils/globalStyles'
import { Box, Button, Container, Modal } from '@mui/material'
import AddRounded from '@mui/icons-material/AddRounded'
import { raisedButtonSx } from '../components/UI/Depth'
import { customStyles } from '../utils/tableCustomStyles'
import { RowActionMenu, ConfirmDialog } from '../components/UI/RowActionMenu'
import EditOutlined from '@mui/icons-material/EditOutlined'
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded'
import TableHeader from '../components/TableHeader'
import Alert from '../components/Alert'
import { modalOverlay, modalCard } from '../utils/modalStyles'

const GET_CATEGORIES = gql`
  ${getRestaurantDetail}
`
const DELETE_CATEGORY = gql`
  ${deleteCategory}
`
// Kept out of the table cell renderer so the menu owns its own state instead of
// adding a hook per row to whatever component happens to render the cell
const ActionButtons = ({ row, onEdit, onDelete, t }) => {
  return (
    <div>
      <RowActionMenu
        label={t('Action')}
        actions={[
          {
            label: t('Edit'),
            icon: EditOutlined,
            tone: 'primary',
            onClick: () => {
              onEdit(row)
            }
          },
          {
            label: t('Delete'),
            icon: DeleteOutlineRounded,
            tone: 'danger',
            confirm: false,
            divider: true,
            onClick: () => {
              onDelete(row)
            }
          }
        ]}
      />
    </div>
  )
}

const Category = props => {
  const { t } = props
  const [editModal, setEditModal] = useState(false)
  const [category, setCategory] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [addModal, setAddModal] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleteError, setDeleteError] = useState('')
  const onChangeSearch = e => setSearchQuery(e.target.value)

  const openEditModal = category => {
    setCategory(category)
    setEditModal(true)
  }
  const closeEditModal = () => {
    setEditModal(false)
    setCategory(null)
  }
  const restaurantId = localStorage.getItem('restaurantId')

  // The mutation returns the restaurant with its full category list, so the
  // table updates from the cache without a refetch
  const [mutate, { loading }] = useMutation(DELETE_CATEGORY, {
    onCompleted: () => {
      setDeleteTarget(null)
      setDeleteError('')
    },
    onError: error => {
      setDeleteError(error.message)
      setTimeout(() => setDeleteError(''), 5000)
    }
  })

  const { data, error: errorQuery, loading: loadingQuery, refetch } = useQuery(
    GET_CATEGORIES,
    {
      variables: {
        id: restaurantId
      }
    }
  )
  const customSort = (rows, field, direction) => {
    const handleField = row => {
      if (row[field]) {
        return row[field].toLowerCase()
      }
      return row[field]
    }
    return orderBy(rows, handleField, direction)
  }
  const columns = [
    {
      name: t('Title'),
      sortable: true,
      selector: 'title'
    },
    {
      name: t('Emoji'),
      cell: row => <span style={{ fontSize: 20 }}>{row.emoji || '-'}</span>
    },
    {
      name: t('Image'),
      cell: row =>
        row.image ? (
          <img
            src={row.image}
            alt={row.title}
            style={{ width: 34, height: 34, objectFit: 'contain' }}
          />
        ) : (
          <span>-</span>
        )
    },
    {
      name: t('Action'),
      cell: row => (
        <ActionButtons
          row={row}
          onEdit={openEditModal}
          onDelete={setDeleteTarget}
          t={t}
        />
      )
    }
  ]

  const regex =
    searchQuery.length > 2 ? new RegExp(searchQuery.toLowerCase(), 'g') : null

  const filtered =
    searchQuery.length < 3
      ? data && data.restaurant.categories
      : data &&
        data.restaurant.categories.filter(category => {
          return category.title.toLowerCase().search(regex) > -1
        })
  const globalClasses = useGlobalStyles()
  return (
    <>
      <Header
        action={
          <Button
            startIcon={<AddRounded />}
            onClick={() => setAddModal(true)}
            sx={raisedButtonSx}>
            {t('Add Category')}
          </Button>
        }
      />
      {deleteError ? <Alert message={deleteError} severity="error" /> : null}
      {/* Page content */}
      <Container className={globalClasses.flex} fluid>
        {errorQuery ? <span>{`Error! ${errorQuery.message}`}</span> : null}
        {loadingQuery ? (
          <CustomLoader />
        ) : (
          <DataTable
            subHeader={true}
            subHeaderComponent={
              <SearchBar
                value={searchQuery}
                onChange={onChangeSearch}
                onClick={() => refetch()}
              />
            }
            title={<TableHeader title={t('Categories')} />}
            columns={columns}
            data={filtered}
            pagination
            progressPending={loading}
            progressComponent={<CustomLoader />}
            sortFunction={customSort}
            defaultSortField="title"
            customStyles={customStyles}
            selectableRows
            paginationIconLastPage=""
            paginationIconFirstPage=""
          />
        )}

        <Modal open={editModal} onClose={closeEditModal} sx={modalOverlay}>
          <Box sx={modalCard('md')}>
            <CategoryComponent category={category} onClose={closeEditModal} />
          </Box>
        </Modal>

        <ConfirmDialog
          open={Boolean(deleteTarget)}
          title={`${t('Delete')} ${deleteTarget?.title || ''}?`}
          message={t('DeleteCategoryConfirm', { title: deleteTarget?.title })}
          confirmLabel={t('Delete')}
          cancelLabel={t('Cancel')}
          loading={loading}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() =>
            mutate({
              variables: {
                id: deleteTarget._id,
                restaurant: restaurantId
              }
            })
          }
        />
        <Modal
          open={addModal}
          onClose={() => setAddModal(false)}
          sx={modalOverlay}>
          <Box sx={modalCard('md')}>
            <CategoryComponent onClose={() => setAddModal(false)} />
          </Box>
        </Modal>
      </Container>
    </>
  )
}
export default withTranslation()(Category)
